/**
 * Hook for managing Event Survey Response validation schema
 *
 * This hook encapsulates all yup validation logic for the event survey form.
 * It follows the pattern established in SurveyCreation containers.
 *
 * @see packages/main/app/web/containers/SurveyCreation/EventRegistrationSurvey/hooks/useEventRegistrationValidation.ts
 */

import * as React from 'react';
import * as yup from 'yup';
import type { IEventRegistrationSurvey } from '../../../../../../../models/Api';
import { areGuestNamesValid, buildCustomPropertiesSchema } from '../utils';

const usPhoneRegExp = new RegExp(/^((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d$/);

interface UseEventSurveyValidationProps {
	survey?: IEventRegistrationSurvey;
}

export const useEventSurveyValidation = ({ survey }: UseEventSurveyValidationProps) => {
	const validationSchema = React.useMemo(() => {
		// Guest limit only applies to free events or legacy paid events (not tiered ticketing)
		// Tiered ticketing events have eventTickets array, so exclude them even if guestLimit is set
		const hasTieredTicketing = survey?.isPaidEvent && survey?.attendeeOptions?.eventTickets?.length;
		const hasGuestLimit =
			survey?.attendeeOptions?.guestLimit &&
			!hasTieredTicketing &&
			(!survey?.isPaidEvent || survey?.attendeeOptions?.costPerAttendee);

		return yup.object().shape({
			isAttending: yup.boolean().required('Response is required'),
			email: yup.string().trim().email('Invalid email').required('Email is required'),
			ticketSelections: hasTieredTicketing
				? yup.object().test('at-least-one-ticket', 'Please select at least one ticket', function (ticketSelections) {
						const userIsAttending = this.parent.isAttending;
						if (!userIsAttending) return true;
						if (!ticketSelections) return false;
						const totalTickets = Object.values(ticketSelections).reduce((sum: number, qty) => sum + (qty as number), 0);
						return totalTickets > 0;
					})
				: undefined,
			guests: hasGuestLimit
				? yup.mixed().when(['isAttending', 'bringingGuest'], {
						is: (isAttending: boolean, bringingGuest: boolean) => isAttending && bringingGuest,
						then: () =>
							yup
								.array()
								.of(
									yup
										.object()
										.shape({
											firstName: yup.string(),
											lastName: yup.string(),
										})
										.test(
											'if-one-entered-both-first-and-last-must-be-defined',
											"If providing a guest's name, both their first and last name must be entered.",
											({ firstName, lastName }) => areGuestNamesValid({ firstName, lastName })
										)
								)
								.max(survey.attendeeOptions.guestLimit!)
								.test('require-all-guest-names', 'All guests must have names provided', function (guests) {
									if (!survey?.attendeeOptions?.requireGuestNames) return true;
									if (!guests || guests.length === 0) return true;
									// All guests must have both firstName and lastName
									return guests.every(guest => guest.firstName && guest.lastName);
								}),
						otherwise: () => yup.array().optional(),
					})
				: undefined,
			firstName: yup.string().required('First name is required'),
			lastName: yup.string().required('Last name is required'),
			phone: survey?.attendeeOptions?.requirePhoneNumber
				? yup.string().trim().matches(usPhoneRegExp, 'Must be a valid phone number').required('Phone is required')
				: yup
						.string()
						.optional()
						.trim()
						.matches(usPhoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
			customProperties: yup.mixed().when('isAttending', {
				is: true,
				then: () =>
					survey?.customForm?.fields
						? buildCustomPropertiesSchema(
								survey.customForm.fields.map(field => ({
									...field,
									id: field.id || '', // Ensure 'id' is defined, provide a default value if necessary
								})),
								survey.surveyForm
							)
						: yup.object().shape({}),
				otherwise: () => yup.object().shape({}),
			}),
		});
	}, [survey]);

	return validationSchema;
};
